import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Store, Plus, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StoreGrid } from '../components/StoreGrid';
import { StoreDetailView } from '../components/StoreDetailView';
import { ConnectStoreModal } from '../components/ConnectStoreModal';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetApiWorkspacesWorkspaceIdStores, getGetApiWorkspacesWorkspaceIdStoresQueryKey } from '@/api/generated/stores/stores';
import { extractStoresArray } from '@/lib/utils';
import { toast } from 'sonner';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { StoreItem } from '@/types';

export const IntegrationsPage: React.FC = () => {
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { activeWorkspaceId, currentRole } = useWorkspaceStore();
  const canManageStores = currentRole === 'Owner' || currentRole === 'Admin';

  const { data: stores, isLoading: isLoadingStores } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, {
    query: { enabled: !!activeWorkspaceId }
  });

  const storesList = extractStoresArray(stores) as StoreItem[];
  const connectedCount = storesList.filter((s) => s.status === 'Connected' || !s.status).length;

  const requestedSettings = searchParams.get('settings') === 'true';
  const selectedStoreId =
    searchParams.get('storeId') ||
    (requestedSettings && storesList.length > 0 ? storesList[0].id : null);
  const selectedStore = selectedStoreId ? storesList.find((s) => s.id === selectedStoreId) : null;

  const handleBackToGrid = () => {
    searchParams.delete('storeId');
    searchParams.delete('settings');
    setSearchParams(searchParams, { replace: true });
  };

  // Handle OAuth callback parameters (e.g. ?code=... or ?connected=true)
  useEffect(() => {
    const code = searchParams.get('code');
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (activeWorkspaceId && (connected === 'true' || code)) {
      if (connected === 'true') {
        toast.success('Integração de loja conectada com sucesso via OAuth!');
      } else if (code) {
        toast.success('Código de autorização OAuth verificado! Loja adicionada ao seu workspace.');
      }
      queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdStoresQueryKey(activeWorkspaceId) });
      if (connected) searchParams.delete('connected');
      if (code) searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
    } else if (error) {
      toast.error(`Falha na conexão OAuth: ${error}`);
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient, activeWorkspaceId]);

  // If a store is selected, render the Store Detail & Configuration view
  if (selectedStoreId) {
    if (isLoadingStores && !selectedStore) {
      return (
        <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
          <div className="flex justify-between items-center pb-4 border-b border-border/80">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-8 w-48" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
              <Skeleton className="h-44 rounded-xl" />
              <Skeleton className="h-96 rounded-xl" />
            </div>
            <div className="hidden lg:block lg:col-span-4">
              <Skeleton className="h-64 rounded-xl" />
            </div>
          </div>
        </div>
      );
    }

    if (selectedStore) {
      return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
          <StoreDetailView store={selectedStore} onBack={handleBackToGrid} />
        </div>
      );
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Top Banner / Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Store className="h-5 w-5 text-primary" />
            Lojas Conectadas
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Conecte suas lojas virtuais para automatizar o atendimento com IA
          </p>
        </div>

        {canManageStores ? (
          <Button
            size="sm"
            className="gap-1.5 shadow-sm font-medium"
            onClick={() => setIsConnectModalOpen(true)}
          >
            <Plus className="h-4 w-4" />
            <span>Conectar Loja</span>
          </Button>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-md border border-border/50">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />
            <span>Apenas Proprietários e Administradores podem gerenciar integrações</span>
          </div>
        )}
      </div>

      {/* Metric Cards Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card/50 backdrop-blur-sm border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Store className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Lojas Conectadas</p>
              <p className="text-lg font-bold text-foreground">{connectedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stores Grid List */}
      <StoreGrid onOpenConnectModal={() => setIsConnectModalOpen(true)} />

      {/* Connect Store Modal */}
      {canManageStores && (
        <ConnectStoreModal
          open={isConnectModalOpen}
          onOpenChange={setIsConnectModalOpen}
          onStoreConnected={() => {
            if (activeWorkspaceId) {
              queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdStoresQueryKey(activeWorkspaceId) });
            }
          }}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;
